/**
 * Measurement utility functions for converting between metric and imperial units
 * and providing appropriate display units based on user preferences
 */

export const MeasurementSystem = {
    METRIC: 'metric',
    IMPERIAL: 'imperial'
};

/**
 * Get the appropriate unit display based on measurement system
 * @param {string} measurementSystem - 'metric' or 'imperial'
 * @param {string} unitType - 'length', 'distance', 'height'
 * @returns {string} The display unit (cm, inch, m, ft, etc.)
 */
export const getDisplayUnit = (measurementSystem, unitType = 'length') => {
    if (measurementSystem === MeasurementSystem.IMPERIAL) {
        switch (unitType) {
            case 'length':
            case 'distance':
                return 'inch';
            case 'height':
                return 'ft';
            case 'row_distance':
            case 'plant_distance':
                return 'inch';
            default:
                return 'inch';
        }
    } else {
        // Default to metric
        switch (unitType) {
            case 'length':
            case 'distance':
            case 'row_distance':
            case 'plant_distance':
                return 'cm';
            case 'height':
                return 'm';
            default:
                return 'cm';
        }
    }
};
/**
 * Format a measurement value with appropriate unit (no conversion)
 * @param {number} value - The value to format
 * @param {string} fromUnit - The original unit ('cm', 'm') - not used for conversion
 * @param {string} measurementSystem - 'metric' or 'imperial'
 * @param {string} unitType - Optional unit type for unit display
 * @returns {string} Formatted string with original value and appropriate unit
 */
export const formatMeasurement = (value, fromUnit, measurementSystem, unitType = 'length') => {
    const displayUnit = getDisplayUnit(measurementSystem, unitType);
    return `${value} ${displayUnit}`;
};

/**
 * Format a dimension in the crop's stored native measurement system.
 * The account system is only a fallback for legacy crop records.
 */
export const formatCropMeasurement = (crop, field, accountMeasurementSystem) => {
    if (crop?.[field] === null || crop?.[field] === undefined) {
        return null;
    }

    const measurementSystem = crop.measurement_system || accountMeasurementSystem;
    return formatMeasurement(crop[field], null, measurementSystem, 'distance');
};

/**
 * Get measurement addon text for Ant Design InputNumber components
 * @param {string} measurementSystem - 'metric' or 'imperial'
 * @param {string} unitType - Optional unit type
 * @returns {string} Unit text for addonAfter prop
 */
export const getMeasurementAddon = (measurementSystem, unitType = 'length') => {
    return getDisplayUnit(measurementSystem, unitType);
};